import { useState } from 'react';
import { NJIconButton } from '@engie-group/fluid-design-system-react';
import PrototypeStudioLayout from '@shared/PrototypeStudioLayout';
import Loading from '@shared/Loading';
import { Size2MixStatusTag, Size2MixTabs, NetworkShell, TEST_PUC_NETWORK, useSize2MixRefNavigation } from '@shared/size2mix-shell';
import { useOptimisationDetail, useOptimisationIds } from './hooks';
import type { DetailTabId } from './types';
import ParametersTab from './ParametersTab';
import MapTab from './MapTab';
import ChartsTab from './ChartsTab';
import SynthesisTab from './SynthesisTab';
import LogsTab from './LogsTab';

export const meta = {
    title: "Référence — Réseau > Détail d'une optimisation",
    description:
        "Page de référence du détail d'une optimisation court terme (PUC EDM) : en-tête avec navigation précédente/suivante, 5 onglets (Paramètres, Résultats cartographiques sur la vraie géométrie, Graphiques MixedChart agrégés, Synthèse, Logs).",
    tags: ['optimization', 'detail'],
    author: 'dv5886',
    status: 'review' as const,
    category: 'optimisations',
    createdAt: '2026-10-10',
};

const TABS: Array<{ id: DetailTabId; label: string }> = [
    { id: 'parameters', label: 'Paramètres' },
    { id: 'map', label: 'Résultats cartographiques' },
    { id: 'charts', label: 'Graphiques' },
    { id: 'synthesis', label: 'Synthèse' },
    { id: 'logs', label: 'Logs' },
];

export default function RefOptimisationDetail() {
    const nav = useSize2MixRefNavigation();
    const { data: ids = [] } = useOptimisationIds();
    const [eid, setEid] = useState('304827981');
    const [tab, setTab] = useState<DetailTabId>('parameters');
    const { data: detail, isLoading } = useOptimisationDetail(eid);

    const pos = ids.indexOf(eid);
    const goRelative = (delta: number) => {
        const next = ids[pos + delta];
        if (next) setEid(next);
    };

    return (
        <PrototypeStudioLayout title={meta.title}>
            <NetworkShell network={TEST_PUC_NETWORK} activePage="optimisations" onNavigate={nav.onNetworkNavigate} onPrimaryNavigate={nav.onPrimaryNavigate}>
                {isLoading || !detail ? (
                    <Loading text="Chargement de l'optimisation" scale="md" />
                ) : (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 8 }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ display: 'inline-flex', gap: 16, marginRight: 8 }}>
                                    {[
                                        { icon: 'navigate_before', label: 'Optimisation précédente', disabled: pos <= 0, delta: -1 },
                                        { icon: 'navigate_next', label: 'Optimisation suivante', disabled: pos < 0 || pos >= ids.length - 1, delta: 1 },
                                    ].map((b) => (
                                        <span key={b.icon} style={{ border: '1px solid var(--nj-core-color-grey-300)', borderRadius: 4, background: 'var(--nj-semantic-color-background-neutral-primary-default)' }}>
                                            <NJIconButton icon={b.icon} label={b.label} variant="tertiary" scale="sm" isDisabled={b.disabled} onClick={() => goRelative(b.delta)} />
                                        </span>
                                    ))}
                                </span>
                                <span className="material-icons" aria-hidden style={{ fontSize: 24 }}>
                                    stacked_bar_chart
                                </span>
                                <span style={{ fontSize: 16, lineHeight: '24px' }} title={`${detail.summary.type} · ${detail.summary.steps} pas`}>{detail.summary.name}</span>
                                <Size2MixStatusTag label={detail.summary.status} />
                            </span>
                            <span style={{ display: 'inline-flex' }}>
                                <NJIconButton icon="table_view" label="Ancienne Version" variant="tertiary" scale="lg" onClick={() => nav.goTo('optimisations')} />
                                <NJIconButton icon="delete_outline" label="Supprimer" variant="tertiary" scale="lg" />
                            </span>
                        </div>
                        <Size2MixTabs tabs={TABS} activeId={tab} onChange={setTab} />
                        <div style={{ padding: tab === 'map' ? 0 : tab === 'parameters' || tab === 'logs' ? 16 : '0 16px' }}>
                            {tab === 'parameters' && <ParametersTab detail={detail} />}
                            {tab === 'map' && <MapTab detail={detail} />}
                            {tab === 'charts' && <ChartsTab detail={detail} />}
                            {tab === 'synthesis' && <SynthesisTab detail={detail} />}
                            {tab === 'logs' && <LogsTab detail={detail} />}
                        </div>
                    </>
                )}
            </NetworkShell>
        </PrototypeStudioLayout>
    );
}
